import React, { createContext, useContext, useCallback, useMemo } from 'react';
import dashboardCache from '../utils/dashboardCache';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

export const CacheContext = createContext();

export const useCache = () => {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
};

export const CacheProvider = ({ children }) => {
  // Use AuthContext to get userId for user-specific caching
  const { user } = useAuth();

  // Get userId, fallback to 'anonymous' if not logged in
  const getUserId = useCallback(() => {
    return user?.id || 'anonymous';
  }, [user]);

  // Check if cache is valid (using dashboardCache)
  const isCacheValid = useCallback((key) => {
    const userId = getUserId();
    const cached = dashboardCache.get(key, userId);
    return cached && !cached.isStale;
  }, [getUserId]);

  // Get cached data (using dashboardCache)
  const getCachedData = useCallback((key) => {
    const userId = getUserId();
    const cached = dashboardCache.get(key, userId);

    if (cached) {
logger.debug('CONTEXT', `Cache hit for ${key}`);
      return cached.data;
    }
logger.debug('CONTEXT', `Cache miss for ${key}`);
    return null;
  }, [getUserId]);

  // Set cache data (using dashboardCache)
  const setCacheData = useCallback((key, data) => {
    const userId = getUserId();
    dashboardCache.set(key, userId, data);
logger.debug('CONTEXT', `Cache set for ${key}`);
  }, [getUserId]);

  // Clear specific cache
  const clearCache = useCallback((key) => {
    const userId = getUserId();

    // Clear from dashboardCache
    dashboardCache.set(key, userId, null);
logger.debug('CONTEXT', `Cache cleared for ${key}`);
  }, [getUserId]);

  // Clear all cache for current user
  const clearAllCache = useCallback(() => {
    const userId = getUserId();
    dashboardCache.clearUserCache(userId);
logger.debug('CONTEXT', 'All cache cleared');
  }, [getUserId]);

  // Cache wrapper for API calls (using dashboardCache with stale-while-revalidate)
  const withCache = useCallback(async (key, apiCall, forceRefresh = false) => {
    const userId = getUserId();

    // If force refresh, clear cache first
    if (forceRefresh) {
      dashboardCache.set(key, userId, null);
    }

    // Use stale-while-revalidate pattern from dashboardCache
    // This returns stale data immediately and fetches fresh in background
    return dashboardCache.getWithRevalidate(key, userId, apiCall);
  }, [getUserId]);

  const value = useMemo(() => ({
    getCachedData,
    setCacheData,
    clearCache,
    clearAllCache,
    withCache,
    isCacheValid,
  }), [getCachedData, setCacheData, clearCache, clearAllCache, withCache, isCacheValid]);

  return <CacheContext.Provider value={value}>{children}</CacheContext.Provider>;
};